// ════════════════════════════════════════════════════════════
//  online/ie.js — Shatterproof's seat in the Empire
// ════════════════════════════════════════════════════════════
//  Who you are (the vendored ieIdentity), what you hold (MINI DUST),
//  which SERVER you're on, and the server's clock. Everything online
//  in Shatterproof reads these four things from here and nowhere else.
//
//  ── LAWS (ie-firebase-rules / ie-cross-game-economy) ─────────
//  · One drawer: every game path derives from BASE (games/shatterproof),
//    which the live `games/$gameId` umbrella already allows — no rules
//    deploy needed. The one exception is the WALLET: currency lives at
//    profiles/{uid}/miniDust (Law 3), owner-sealed, so a player's
//    client is the only thing that can ever write their Mini Dust.
//  · Mini Dust is the title's ONE currency (THE TWO-CURRENCY LAW):
//    it buys, it levels (later), and it is the betting chip.
//  · Delta transactions on the leaf, never read-modify-write
//    (ieEconomy.deltaCurrency). A debit that would go below 0 is
//    refused — refusal is an answer, not an error.
//  · Writes only when `writable` (really signed in). A guest pass from
//    the Pier can watch and read, never spend.
// ════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from "react";
import { ref, onValue, set, runTransaction } from "firebase/database";
import { db } from "../firebase";
import { useIEAuth } from "../ieIdentity";
import { deltaCurrency } from "../ieEconomy";
import { STARTER_PURSE } from "./dials.js";

export const GAME_ID = "shatterproof";
export const BASE = `games/${GAME_ID}`;

// ── MINI DUST ────────────────────────────────────────────────
// The row is also seeded in gamemaster/PIERRE_RATES_SEED.json (the Well
// reads it from there once caP'n imports it). Passing it as `rates`
// lets deltaCurrency resolve the path before the Well knows us.
export const MINI_DUST = {
  currencyId: "MD", name: "Mini Dust", short: "MD", icon: "✶",
  owningApp: GAME_ID, path: "miniDust", tint: "#7ce7ff", perCrest: 12, provisional: true,
};
// ── KING'S KRESTS ────────────────────────────────────────────
// caP'n 2026-10-20: "Yes, call it King's Krests, for now everything still cost 1 even this currency,
// however, in the future this one will be worth more than Mini Dust". Earned by DOING something
// (clearing a Climb rung, the summit, and a player's win in the Circuit) — never by grinding wins.
// ⚑ A deliberate exception to the Two-Currency Law (Mini Dust + ◆ is the usual pair), flagged to
// caP'n 2026-10-20. Seeded in gamemaster/PIERRE_RATES_SEED.json like Mini Dust.
export const KINGS_KRESTS = {
  currencyId: "KK", name: "King's Krests", short: "KK", icon: "♛",
  owningApp: GAME_ID, path: "kingsKrests", tint: "#f2c14e", perCrest: 0.5, provisional: true,
};
const RATES = { MD: MINI_DUST, KK: KINGS_KRESTS };
export { STARTER_PURSE, WIN_PURSE } from "./dials.js";

export const fmtDust = (n) => `${MINI_DUST.icon} ${Math.floor(n || 0).toLocaleString()}`;
export const fmtKrests = (n) => `${KINGS_KRESTS.icon} ${Math.floor(n || 0).toLocaleString()}`;

/** Add (or, negative, spend) Mini Dust. Resolves the new balance, or null if refused. */
export function deltaDust(uid, amount) {
  return deltaCurrency(uid, "MD", amount, { owningApp: GAME_ID, rates: RATES });
}
/** Add (or spend) King's Krests. Resolves the new balance, or null if refused. */
export function deltaKrests(uid, amount) {
  return deltaCurrency(uid, "KK", amount, { owningApp: GAME_ID, rates: RATES });
}

// ── SERVERS ──────────────────────────────────────────────────
// caP'n: "every server will have their own, when a friend joins your
// party they join your server". Today there is ONE server; the seam is
// that every hourly tournament and every room is keyed by server id,
// and a party carries the leader's server for everyone in it.
export const SERVERS = ["1"];         // ⟵ add "2", "3"… when the Empire needs them
export const HOME_SERVER = SERVERS[0];

// ── THE SERVER'S CLOCK ───────────────────────────────────────
// Every client must agree on "now" to the second (a tournament starts
// on the hour for EVERYONE). Device clocks drift by minutes; Firebase's
// own offset estimate fixes that for free.
let _offset = 0, _armed = false, _known = false;
function armClock() {
  if (_armed) return; _armed = true;
  onValue(ref(db, ".info/serverTimeOffset"), (s) => { const v = s.val(); _offset = Number.isFinite(v) ? v : 0; _known = true; }, () => {});
}
export const serverNow = () => Date.now() + _offset + (process.env.NODE_ENV !== "production" && typeof window !== "undefined" && window.__ieDevShift || 0);   // __ieDevShift: dev-only fake clock (ui/leaguePreview.jsx)
// has the server told us its clock yet? (until then a device running fast could seed a fight before its bell)
export const clockKnown = () => _known;

// ════════════════════════════════════════════════════════════
//  useOnline — the one hook App mounts
// ════════════════════════════════════════════════════════════
export function useOnline() {
  const auth = useIEAuth({ gameId: GAME_ID, createProfile: true });
  const { authUid, writable, handoffPartyId } = auth;
  const [dust, setDust] = useState(null);         // null = not known yet (never "0" by accident)
  const [krests, setKrests] = useState(null);
  const [server, setServer] = useState(HOME_SERVER);
  const [partyId] = useState(() => handoffPartyId || null);
  const purseDone = useRef(false);

  useEffect(() => { armClock(); }, []);

  // the wallet, live
  useEffect(() => {
    if (!authUid) { setDust(null); return undefined; }
    return onValue(ref(db, `profiles/${authUid}/${MINI_DUST.path}`), (s) => setDust(s.exists() ? (s.val() || 0) : 0), () => {});
  }, [authUid]);
  useEffect(() => {
    if (!authUid) { setKrests(null); return undefined; }
    return onValue(ref(db, `profiles/${authUid}/${KINGS_KRESTS.path}`), (s) => setKrests(s.exists() ? (s.val() || 0) : 0), () => {});
  }, [authUid]);

  // the starter purse, once per soul
  useEffect(() => {
    if (!writable || !authUid || purseDone.current) return;
    purseDone.current = true;
    (async () => {
      // ONE tab wins the claim (a transaction), then pays; if the pay is refused
      // the claim is taken back so the next visit tries again
      const flag = ref(db, `${BASE}/players/${authUid}/starterPurse`);
      const c = await runTransaction(flag, (cur) => (cur == null ? { amount: STARTER_PURSE, at: serverNow() } : undefined)).catch(() => null);
      if (!c || !c.committed) { if (!c) purseDone.current = false; return; }
      const after = await deltaDust(authUid, STARTER_PURSE).catch(() => null);
      if (after == null) await set(flag, null).catch(() => {});
    })();
  }, [writable, authUid]);

  // the server: a party rides on its leader's server. The first member
  // to look stamps the leader's choice (everyone is on HOME_SERVER today).
  useEffect(() => {
    if (!partyId || !authUid) return undefined;
    return onValue(ref(db, `parties/${partyId}/server`), (s) => {
      const v = s.val();
      if (v && SERVERS.includes(String(v))) setServer(String(v));
      else if (!v) runTransaction(ref(db, `parties/${partyId}/server`), (cur) => cur || HOME_SERVER).catch(() => {});
    }, () => {});
  }, [partyId, authUid]);

  return { ...auth, dust, krests, server, partyId };
}
